import { BasicBeamData, ValidArcBeamData, ValidCurveBeamData } from './investigationsApi';
import { BeamParams, BeamsParams, CurveParams } from '@dmc/ui';

/**
 * A factory class for converting API beam data into params for the ObjectWithBeams class and ObjectBeams components.
 * Also includes type guards for verifying beam data.
 */
export class BeamDataFactory {
  private constructor() {
    // Prevent instantiation
  }

  public static getBeamsParams = (beamData: BasicBeamData): BeamsParams | undefined => {
    if (BeamDataFactory.isValidArcBeamData(beamData)) {
      return BeamDataFactory.getArcBeamsParams(beamData);
    } else if (BeamDataFactory.isValidCurveBeamData(beamData)) {
      return BeamDataFactory.getCurveBeamsParams(beamData);
    } else {
      return undefined;
    }
  };

  public static getArcBeamsParams = ({ Arc }: ValidArcBeamData): BeamsParams => {
    const alarm: BeamParams = {
      arc: {
        radius_m: Arc.AlarmBeamRadius,
        startAngle_deg: Arc.AlarmStartAngle,
        arcLength_deg: Arc.AlarmArcLength,
      },
    };

    const warn: BeamParams = {
      arc: {
        radius_m: Arc.WarnBeamRadius,
        startAngle_deg: Arc.WarnStartAngle,
        arcLength_deg: Arc.WarnArcLength,
      },
    };

    return { alarm, warn, tof: {} };
  };

  public static getCurveBeamsParams = ({
    Curved,
    CurveRadius,
  }: ValidCurveBeamData): BeamsParams => {
    const commonCurveParams: Pick<CurveParams, 'curve_rad_m' | 'beamMode'> = {
      curve_rad_m: CurveRadius,
      beamMode: {
        RearBeamEnabled: Curved.RearBeamEnabled,
        FrontBeamEnabled: Curved.FrontBeamEnabled,
        CurvedBeamEnabled: Curved.CurvedBeamEnabled,
      },
    };

    const alarm: BeamParams = {
      curve: {
        ...commonCurveParams,
        length_m: Curved.AlarmBeamLength,
        buff_m: Curved.AlarmBeamBuffer,
        dw_m: Curved.AlarmBeamDW,
      },
    };

    const warn: BeamParams = {
      curve: {
        ...commonCurveParams,
        length_m: Curved.WarnBeamLength,
        buff_m: Curved.WarnBeamBuffer,
        dw_m: Curved.WarnBeamDW,
      },
    };

    return { alarm, warn, tof: {} };
  };

  public static isValidArcBeamData = (data: BasicBeamData): data is ValidArcBeamData => {
    return data.Arc !== null && data.Curved === null && data.InteractionState !== null;
  };

  public static isValidCurveBeamData = (data: BasicBeamData): data is ValidCurveBeamData => {
    return (
      data.Arc === null &&
      data.Curved !== null &&
      data.InteractionState !== null &&
      data.CurveRadius !== null
    );
  };
}
